<template>
  <v-container fluid>
    <v-card class="pa-6">
      <!-- Header -->
      <v-row align="center" class="mb-4">
        <v-col>
          <div class="text-h5 font-weight-bold">
            ✏️ Edit Matter Data Sheet
          </div>
          <div class="caption grey--text">
            Entity Type: <strong>{{ entityTypeLabel }}</strong>
          </div>
        </v-col>
        <v-col class="d-flex justify-end">
          <v-btn
            outlined
            @click="$router.push(`/incorporate-matter-view/${matterId}`)"
          >
            <v-icon left>mdi-arrow-left</v-icon>ยกเลิก
          </v-btn>
        </v-col>
      </v-row>

      <div v-if="pageLoading" class="text-center pa-10">
        <v-progress-circular
          indeterminate
          color="primary"
        ></v-progress-circular>
      </div>

      <div v-else>
        <!-- ==================== INDIVIDUAL FORM ==================== -->
        <div v-if="isIndividual">
          <v-card outlined class="pa-6" style="max-width:780px;">
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.client_name"
                  :label="
                    hasDockets
                      ? 'Client Name | ชื่อลูกค้า (ไม่สามารถแก้ไขได้ — มี Docket แล้ว)'
                      : 'Client Name | ชื่อลูกค้า *'
                  "
                  outlined
                  dense
                  :disabled="hasDockets"
                  :rules="[rules.required]"
                ></v-text-field>
                <div v-if="hasDockets" class="caption orange--text mt-1 mb-2">
                  <v-icon x-small color="orange darken-2"
                    >mdi-lock-outline</v-icon
                  >
                  Matter นี้มี Docket ผูกอยู่แล้ว —
                  ไม่สามารถเปลี่ยนชื่อลูกค้าได้
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  :value="entityTypeLabel"
                  label="Entity Type"
                  outlined
                  dense
                  readonly
                  background-color="grey lighten-4"
                ></v-text-field>
              </v-col>
            </v-row>

            <div class="form-section-title mb-3">👤 INDIVIDUAL INFORMATION</div>

            <div class="mb-4">
              <div class="caption font-weight-medium mb-2">
                ประเภทเอกสารระบุตัวตน *
              </div>
              <v-row>
                <v-col cols="12" md="6">
                  <div
                    :class="[
                      'radio-option',
                      form.id_type === 'national_id' ? 'radio-selected' : '',
                    ]"
                    @click="form.id_type = 'national_id'"
                  >
                    <v-icon class="mr-2">{{
                      form.id_type === "national_id"
                        ? "mdi-radiobox-marked"
                        : "mdi-radiobox-blank"
                    }}</v-icon>
                    <div>
                      <span class="font-weight-medium">🪪 บัตรประชาชน</span>
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" md="6">
                  <div
                    :class="[
                      'radio-option',
                      form.id_type === 'passport' ? 'radio-selected' : '',
                    ]"
                    @click="form.id_type = 'passport'"
                  >
                    <v-icon class="mr-2">{{
                      form.id_type === "passport"
                        ? "mdi-radiobox-marked"
                        : "mdi-radiobox-blank"
                    }}</v-icon>
                    <div>
                      <span class="font-weight-medium">🛂 หนังสือเดินทาง</span>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </div>

            <v-row v-if="form.id_type === 'national_id'">
              <v-col cols="12" md="6">
                <!-- ⭐ แก้ไข — เพิ่มการกรองเฉพาะตัวเลข + ใส่ขีดคั่นอัตโนมัติ
                     (X-XXXX-XXXXX-XX-X) เหมือนกับ IncorporateMatterAdd.vue
                     เดิมฟิลด์นี้มีแค่ maxlength="17" เฉย ๆ พิมพ์ตัวอักษร
                     หรือสัญลักษณ์เข้าไปได้ตามใจ ไม่บังคับจริง -->
                <v-text-field
                  v-model="form.national_id"
                  label="เลขบัตรประชาชน (13 หลัก) *"
                  outlined
                  dense
                  placeholder="X-XXXX-XXXXX-XX-X"
                  maxlength="17"
                  hint="กรอกเฉพาะตัวเลข 13 หลัก"
                  persistent-hint
                  :rules="[rules.required]"
                  @input="formatNationalId"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.nationality"
                  label="Nationality | สัญชาติ"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row v-else>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.passport_no"
                  label="เลขที่หนังสือเดินทาง *"
                  outlined
                  dense
                  maxlength="15"
                  :rules="[rules.required]"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.nationality"
                  label="Nationality | สัญชาติ"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="form.contact_address"
                  label="Contact Address"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.contact_number"
                  label="Contact Number"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.contact_email"
                  label="Contact Email"
                  outlined
                  dense
                  type="email"
                ></v-text-field>
              </v-col>
            </v-row>

            <!-- Actions -->
            <v-row class="mt-4">
              <v-col class="d-flex justify-end" style="gap:10px;">
                <v-btn
                  color="#0A7EA5"
                  dark
                  :loading="loadingSave"
                  @click="saveEdit"
                >
                  <v-icon left>mdi-content-save</v-icon>Save
                </v-btn>
                <v-btn
                  outlined
                  @click="$router.push(`/incorporate-matter-view/${matterId}`)"
                  >Cancel</v-btn
                >
              </v-col>
            </v-row>
          </v-card>
        </div>

        <!-- ==================== COMPANY FORM ==================== -->
        <div v-else>
          <!-- Client -->
          <v-row class="mb-2">
            <v-col cols="12" md="6">
              <v-autocomplete
                v-model="selectedClient"
                :items="clientList"
                item-text="text"
                item-value="value"
                :label="
                  hasDockets
                    ? 'Client Name | ชื่อลูกค้า (ไม่สามารถแก้ไขได้ — มี Docket แล้ว)'
                    : 'Client Name | ชื่อลูกค้า'
                "
                outlined
                dense
                :clearable="!hasDockets"
                :disabled="hasDockets"
                return-object
                @change="onClientChange"
                @click:clear="onClientClear"
              ></v-autocomplete>
              <div v-if="hasDockets" class="caption orange--text mt-1">
                <v-icon x-small color="orange darken-2"
                  >mdi-lock-outline</v-icon
                >
                Matter นี้มี Docket ผูกอยู่แล้ว — ไม่สามารถเปลี่ยนชื่อลูกค้าได้
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                :value="entityTypeLabel"
                label="Entity Type"
                outlined
                dense
                readonly
                background-color="grey lighten-4"
              ></v-text-field>
            </v-col>
          </v-row>

          <!-- Company Info -->
          <div class="form-section-title mb-3">🏢 COMPANY INFORMATION</div>
          <v-row>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="form.company_name"
                label="Company Name *"
                outlined
                dense
                :rules="[rules.required]"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="form.registration_no"
                label="Registration No. *"
                outlined
                dense
                :rules="[rules.required]"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="form.date_of_incorporation"
                label="Date of Incorporation"
                outlined
                dense
                type="date"
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.registered_address"
                label="Registered Address *"
                outlined
                dense
                :rules="[rules.required]"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.branch"
                label="Branch"
                outlined
                dense
              ></v-text-field>
            </v-col>
          </v-row>

          <!-- Capital -->
          <div class="form-section-title mb-3 mt-2">💰 CAPITAL</div>
          <v-row>
            <v-col cols="12" md="3">
              <v-text-field
                v-model="form.registered_capital"
                label="Registered Capital (THB)"
                outlined
                dense
                type="number"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                v-model="form.number_of_shares"
                label="Number of Shares"
                outlined
                dense
                type="number"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                v-model="form.par_value_per_share"
                label="Par Value per Share"
                outlined
                dense
                type="number"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                v-model="form.paid_up_capital"
                label="Paid-up Capital (THB)"
                outlined
                dense
                type="number"
              ></v-text-field>
            </v-col>
          </v-row>

          <!-- Shareholders -->
          <div class="form-section-title mb-3 mt-2">👥 SHAREHOLDERS</div>
          <v-simple-table dense class="mb-2">
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="bg-colorth tabletextwhite">FULL NAME</th>
                  <th class="bg-colorth tabletextwhite">NATIONALITY</th>
                  <th class="bg-colorth tabletextwhite">ORDINARY SHARES</th>
                  <th class="bg-colorth tabletextwhite">PREFERRED SHARES</th>
                  <th
                    class="bg-colorth tabletextwhite"
                    style="width:50px;"
                  ></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(sh, i) in form.shareholders" :key="'sh' + i">
                  <td>
                    <v-text-field
                      v-model="sh.full_name"
                      dense
                      hide-details
                      solo
                      flat
                      placeholder="ชื่อ–นามสกุล"
                    ></v-text-field>
                  </td>
                  <td>
                    <v-text-field
                      v-model="sh.nationality"
                      dense
                      hide-details
                      solo
                      flat
                      placeholder="สัญชาติ"
                    ></v-text-field>
                  </td>
                  <td>
                    <v-text-field
                      v-model="sh.ordinary_shares"
                      dense
                      hide-details
                      solo
                      flat
                      type="number"
                      placeholder="0"
                    ></v-text-field>
                  </td>
                  <td>
                    <v-text-field
                      v-model="sh.preferred_shares"
                      dense
                      hide-details
                      solo
                      flat
                      type="number"
                      placeholder="0"
                    ></v-text-field>
                  </td>
                  <td>
                    <v-btn
                      icon
                      x-small
                      color="red"
                      @click="removeShareholder(i)"
                      ><v-icon>mdi-close</v-icon></v-btn
                    >
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
          <v-btn outlined small @click="addShareholder" class="mb-4"
            ><v-icon left small>mdi-plus</v-icon>Add Shareholder</v-btn
          >

          <!-- Directors -->
          <div class="form-section-title mb-3 mt-2">👔 DIRECTORS</div>
          <v-simple-table dense class="mb-2">
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="bg-colorth tabletextwhite">FULL NAME</th>
                  <th class="bg-colorth tabletextwhite">NATIONALITY</th>
                  <th class="bg-colorth tabletextwhite">AUTHORIZED</th>
                  <th
                    class="bg-colorth tabletextwhite"
                    style="width:50px;"
                  ></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(dir, i) in form.directors" :key="'dir' + i">
                  <td>
                    <v-text-field
                      v-model="dir.full_name"
                      dense
                      hide-details
                      solo
                      flat
                      placeholder="ชื่อ–นามสกุล"
                    ></v-text-field>
                  </td>
                  <td>
                    <v-text-field
                      v-model="dir.nationality"
                      dense
                      hide-details
                      solo
                      flat
                      placeholder="สัญชาติ"
                    ></v-text-field>
                  </td>
                  <td>
                    <v-select
                      v-model="dir.authorized"
                      :items="['-- เลือก --', 'Yes', 'No']"
                      dense
                      hide-details
                      solo
                      flat
                    ></v-select>
                  </td>
                  <td>
                    <v-btn icon x-small color="red" @click="removeDirector(i)"
                      ><v-icon>mdi-close</v-icon></v-btn
                    >
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
          <v-btn outlined small @click="addDirector" class="mb-4"
            ><v-icon left small>mdi-plus</v-icon>Add Director</v-btn
          >

          <!-- Auditor -->
          <div class="form-section-title mb-3 mt-2">📋 AUDITOR</div>
          <v-row>
            <v-col cols="12" md="3"
              ><v-text-field
                v-model="form.audit_firm"
                label="Audit Firm"
                outlined
                dense
              ></v-text-field
            ></v-col>
            <v-col cols="12" md="3"
              ><v-text-field
                v-model="form.licensed_auditor_name"
                label="Licensed Auditor's Name"
                outlined
                dense
              ></v-text-field
            ></v-col>
            <v-col cols="12" md="3"
              ><v-text-field
                v-model="form.auditor_license_no"
                label="Auditor's License No."
                outlined
                dense
              ></v-text-field
            ></v-col>
            <v-col cols="12" md="3"
              ><v-text-field
                v-model="form.fiscal_year_end"
                label="Fiscal Year End"
                outlined
                dense
                type="date"
              ></v-text-field
            ></v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="6">
              <!-- ⭐ แก้ไข — เพิ่มการกรองเฉพาะตัวเลข + ใส่ขีดคั่นอัตโนมัติ
                   เหมือนกับ IncorporateMatterAdd.vue เดิมฟิลด์นี้มีแค่
                   maxlength="17" เฉย ๆ ไม่บังคับรูปแบบจริง -->
              <v-text-field
                v-model="form.auditor_national_id"
                label="Auditor's National ID (13 digits)"
                outlined
                dense
                placeholder="X-XXXX-XXXXX-XX-X"
                maxlength="17"
                hint="กรอกเฉพาะตัวเลข 13 หลัก"
                persistent-hint
                @input="formatAuditorNationalId"
              ></v-text-field>
            </v-col>
          </v-row>

          <!-- Accountant -->
          <div class="form-section-title mb-3 mt-2">📊 ACCOUNTANT</div>
          <v-row>
            <v-col cols="12" md="4"
              ><v-text-field
                v-model="form.accountant_firm"
                label="Accountant Firm"
                outlined
                dense
              ></v-text-field
            ></v-col>
            <v-col cols="12" md="4"
              ><v-text-field
                v-model="form.accountant_name"
                label="Accountant Name"
                outlined
                dense
              ></v-text-field
            ></v-col>
            <v-col cols="12" md="4">
              <!-- ⭐ แก้ไข — เพิ่มการกรองเฉพาะตัวเลข + ใส่ขีดคั่นอัตโนมัติ
                   เหมือนกับ IncorporateMatterAdd.vue (เดิม label ไม่มีคำว่า
                   "(13 digits)" ด้วย เลยเพิ่มให้ตรงกันทั้ง 2 หน้า) -->
              <v-text-field
                v-model="form.accountant_national_id"
                label="Accountant's National ID (13 digits)"
                outlined
                dense
                placeholder="X-XXXX-XXXXX-XX-X"
                maxlength="17"
                hint="กรอกเฉพาะตัวเลข 13 หลัก"
                persistent-hint
                @input="formatAccountantNationalId"
              ></v-text-field>
            </v-col>
          </v-row>

          <!-- VAT -->
          <div class="form-section-title mb-3 mt-2">🧾 VAT REGISTRATION</div>
          <v-row>
            <v-col cols="12" md="4"
              ><v-text-field
                v-model="form.vat_issued_on"
                label="VAT Issued on"
                outlined
                dense
                type="date"
              ></v-text-field
            ></v-col>
          </v-row>

          <!-- Foreign Business -->
          <div class="form-section-title mb-3 mt-2">🌐 FOREIGN BUSINESS</div>
          <v-card
            outlined
            class="pa-3 mb-3"
            :class="form.is_foreign_business ? 'blue lighten-5' : ''"
            style="cursor:pointer;"
            @click="form.is_foreign_business = !form.is_foreign_business"
          >
            <div class="d-flex align-center">
              <v-checkbox
                v-model="form.is_foreign_business"
                hide-details
                class="ma-0 pa-0 mr-3"
                @click.stop
              ></v-checkbox>
              <div>
                <div class="font-weight-medium" style="font-size:13px;">
                  บริษัทต่างด้าว / Foreign Business
                </div>
                <div class="caption grey--text">
                  กาเครื่องหมายหากบริษัทเป็นกิจการที่คนต่างด้าวถือหุ้นเกิน 49%
                </div>
              </div>
            </div>
          </v-card>

          <v-card
            v-if="form.is_foreign_business"
            outlined
            class="pa-4 mb-4 blue lighten-5"
          >
            <div class="font-weight-bold blue--text mb-4">
              📎 เอกสารบริษัทต่างด้าว (อัปโหลดได้มากกว่า 1 ไฟล์ต่อรายการ)
            </div>

            <!-- ── FBL (License) — หลายไฟล์ ── -->
            <div class="mb-5">
              <div class="caption font-weight-bold mb-2">
                3.1 ใบอนุญาตประกอบธุรกิจคนต่างด้าว (Foreign Business License)
              </div>

              <!-- รายการไฟล์เดิม (จาก DB) -->
              <div
                v-for="f in form.fbl_existing_list"
                :key="'fbl-existing-' + f.id"
                class="file-preview-box mb-2"
              >
                <div class="d-flex align-center justify-space-between">
                  <div class="d-flex align-center" style="gap:8px;">
                    <v-icon
                      :color="
                        isPdf(f.file_name) ? 'red darken-1' : 'blue darken-1'
                      "
                    >
                      {{
                        isPdf(f.file_name)
                          ? "mdi-file-pdf-box"
                          : "mdi-file-image"
                      }}
                    </v-icon>
                    <div>
                      <div class="caption font-weight-medium">
                        {{ f.original_name || f.file_name }}
                      </div>
                      <div class="caption grey--text">ไฟล์ปัจจุบัน</div>
                    </div>
                  </div>
                  <div class="d-flex align-center" style="gap:4px;">
                    <v-btn
                      icon
                      x-small
                      color="primary"
                      :href="getFileUrl(f.file_name)"
                      target="_blank"
                      title="เปิดไฟล์"
                    >
                      <v-icon small>mdi-open-in-new</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      x-small
                      color="red"
                      :loading="deletingFileId === f.id"
                      @click="deleteExistingForeignBusinessFile(f)"
                      title="ลบไฟล์"
                    >
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </div>
                <v-img
                  v-if="!isPdf(f.file_name)"
                  :src="getFileUrl(f.file_name)"
                  max-height="180"
                  contain
                  class="mt-2 rounded"
                  style="background:#f3f4f6;"
                ></v-img>
              </div>
              <div
                v-if="form.fbl_existing_list.length === 0"
                class="caption grey--text mb-2"
              >
                ยังไม่มีไฟล์
              </div>

              <!-- เพิ่มไฟล์ใหม่ -->
              <v-btn outlined small @click="$refs.fblInput.click()">
                <v-icon left small>mdi-upload</v-icon>เพิ่มไฟล์
                (เลือกได้หลายไฟล์)
              </v-btn>
              <input
                ref="fblInput"
                type="file"
                multiple
                accept=".pdf,.jpg,.jpeg,.png"
                style="display:none;"
                @change="onFblFileChange"
              />

              <!-- รายการไฟล์ใหม่ที่ยังไม่ได้ Save -->
              <div
                v-for="(f, idx) in form.fbl_new_files"
                :key="'fbl-new-' + idx"
                class="file-preview-box mt-2"
              >
                <div class="d-flex align-center justify-space-between">
                  <div class="d-flex align-center" style="gap:8px;">
                    <v-icon
                      :color="isPdf(f.name) ? 'red darken-1' : 'blue darken-1'"
                    >
                      {{
                        isPdf(f.name) ? "mdi-file-pdf-box" : "mdi-file-image"
                      }}
                    </v-icon>
                    <div>
                      <div class="caption font-weight-medium">{{ f.name }}</div>
                      <div class="caption grey--text">ไฟล์ใหม่ที่เลือก</div>
                    </div>
                  </div>
                  <v-btn
                    icon
                    x-small
                    color="red"
                    @click="clearNewFile('fbl', idx)"
                  >
                    <v-icon small>mdi-close</v-icon>
                  </v-btn>
                </div>
              </div>
            </div>

            <!-- ── FBC (Certificate) — หลายไฟล์ ── -->
            <div>
              <div class="caption font-weight-bold mb-2">
                3.2 หนังสือรับรองประกอบธุรกิจคนต่างด้าว (Foreign Business
                Certificate)
              </div>

              <!-- รายการไฟล์เดิม (จาก DB) -->
              <div
                v-for="f in form.fbc_existing_list"
                :key="'fbc-existing-' + f.id"
                class="file-preview-box mb-2"
              >
                <div class="d-flex align-center justify-space-between">
                  <div class="d-flex align-center" style="gap:8px;">
                    <v-icon
                      :color="
                        isPdf(f.file_name) ? 'red darken-1' : 'blue darken-1'
                      "
                    >
                      {{
                        isPdf(f.file_name)
                          ? "mdi-file-pdf-box"
                          : "mdi-file-image"
                      }}
                    </v-icon>
                    <div>
                      <div class="caption font-weight-medium">
                        {{ f.original_name || f.file_name }}
                      </div>
                      <div class="caption grey--text">ไฟล์ปัจจุบัน</div>
                    </div>
                  </div>
                  <div class="d-flex align-center" style="gap:4px;">
                    <v-btn
                      icon
                      x-small
                      color="primary"
                      :href="getFileUrl(f.file_name)"
                      target="_blank"
                      title="เปิดไฟล์"
                    >
                      <v-icon small>mdi-open-in-new</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      x-small
                      color="red"
                      :loading="deletingFileId === f.id"
                      @click="deleteExistingForeignBusinessFile(f)"
                      title="ลบไฟล์"
                    >
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </div>
                <v-img
                  v-if="!isPdf(f.file_name)"
                  :src="getFileUrl(f.file_name)"
                  max-height="180"
                  contain
                  class="mt-2 rounded"
                  style="background:#f3f4f6;"
                ></v-img>
              </div>
              <div
                v-if="form.fbc_existing_list.length === 0"
                class="caption grey--text mb-2"
              >
                ยังไม่มีไฟล์
              </div>

              <!-- เพิ่มไฟล์ใหม่ -->
              <v-btn outlined small @click="$refs.fbcInput.click()">
                <v-icon left small>mdi-upload</v-icon>เพิ่มไฟล์
                (เลือกได้หลายไฟล์)
              </v-btn>
              <input
                ref="fbcInput"
                type="file"
                multiple
                accept=".pdf,.jpg,.jpeg,.png"
                style="display:none;"
                @change="onFbcFileChange"
              />

              <!-- รายการไฟล์ใหม่ที่ยังไม่ได้ Save -->
              <div
                v-for="(f, idx) in form.fbc_new_files"
                :key="'fbc-new-' + idx"
                class="file-preview-box mt-2"
              >
                <div class="d-flex align-center justify-space-between">
                  <div class="d-flex align-center" style="gap:8px;">
                    <v-icon
                      :color="isPdf(f.name) ? 'red darken-1' : 'blue darken-1'"
                    >
                      {{
                        isPdf(f.name) ? "mdi-file-pdf-box" : "mdi-file-image"
                      }}
                    </v-icon>
                    <div>
                      <div class="caption font-weight-medium">{{ f.name }}</div>
                      <div class="caption grey--text">ไฟล์ใหม่ที่เลือก</div>
                    </div>
                  </div>
                  <v-btn
                    icon
                    x-small
                    color="red"
                    @click="clearNewFile('fbc', idx)"
                  >
                    <v-icon small>mdi-close</v-icon>
                  </v-btn>
                </div>
              </div>
            </div>
          </v-card>

          <!-- Status -->
          <v-row class="mt-2">
            <v-col cols="12" md="3">
              <v-select
                v-model="form.status"
                :items="['Active', 'Inactive']"
                label="Status"
                outlined
                dense
              ></v-select>
            </v-col>
          </v-row>

          <!-- Actions -->
          <v-row class="mt-4">
            <v-col class="d-flex justify-end" style="gap:10px;">
              <v-btn
                color="#0A7EA5"
                dark
                :loading="loadingSave"
                @click="saveEdit"
              >
                <v-icon left>mdi-content-save</v-icon>Save
              </v-btn>
              <v-btn
                outlined
                @click="$router.push(`/incorporate-matter-view/${matterId}`)"
                >Cancel</v-btn
              >
            </v-col>
          </v-row>
        </div>
      </div>
    </v-card>

    <SuccessDialog
      :status="dialogAdd"
      :text_color="text_color"
      :title="title"
      :message="message"
    />
    <SuccessDialogPush
      :status="dialogAddPush"
      :text_color="text_color"
      :title="title"
      :message="message"
      :link="link"
    />
  </v-container>
</template>

<script>
import SuccessDialog from "@/components/cards/SuccessDialog";
import SuccessDialogPush from "@/components/cards/SuccessDialogPush";
import { server } from "@/services/constants";
import {
  getIncorporateMatterById,
  updateIncorporateMatter,
  // ⭐ เพิ่มใหม่ — ลบไฟล์ Foreign Business เดี่ยวทันที ไม่ต้องรอกด Save
  deleteIncorporateMatterForeignBusinessFile,
} from "@/services/api-dtime/api_incorporate";
import { getAllCustomerListByCompany } from "@/services/api-dtime/api_customer";

// ✅ ไฟล์ incorporate อยู่ที่ production storage port 3001
const INCORPORATE_MATTER_FILE_URL = process.env.VUE_APP_PRODUCTION_STORAGE_URL
  ? `${process.env.VUE_APP_PRODUCTION_STORAGE_URL}/incorporatematter`
  : "http://localhost:3001/incorporatematter";

export default {
  name: "incorporate-matter-edit",
  components: { SuccessDialog, SuccessDialogPush },
  data() {
    return {
      matterId: null,
      pageLoading: true,
      loadingSave: false,
      clientList: [],
      selectedClient: null,
      // ⭐ เพิ่มใหม่ — เก็บรายการ docket ของ matter นี้ เพื่อเช็คว่าควร disable
      // ช่อง Client Name หรือไม่ (ถ้ามี docket แล้วห้ามแก้ไขชื่อลูกค้า)
      dockets: [],
      // ⭐ เพิ่มใหม่ — เก็บ id ของไฟล์ Foreign Business ที่กำลังลบอยู่ (โหลด spinner ปุ่มลบ)
      deletingFileId: null,

      // ✅ เพิ่ม: validation rule สำหรับฟิลด์ที่ label มีเครื่องหมาย * (บังคับกรอก)
      rules: {
        required: (v) =>
          (v !== null && v !== undefined && String(v).trim() !== "") ||
          "กรุณากรอกข้อมูล",
      },

      form: {
        client_id: null,
        client_name: "",
        client_name_en: "",
        entity_type: "",
        status: "Active",
        // individual
        id_type: "national_id",
        full_name: "",
        nationality: "",
        national_id: "",
        passport_no: "",
        contact_address: "",
        contact_number: "",
        contact_email: "",
        // company
        company_name: "",
        registration_no: "",
        date_of_incorporation: "",
        registered_address: "",
        branch: "",
        registered_capital: 0,
        number_of_shares: 0,
        par_value_per_share: 0,
        paid_up_capital: 0,
        shareholders: [],
        directors: [],
        audit_firm: "",
        licensed_auditor_name: "",
        auditor_license_no: "",
        fiscal_year_end: "",
        auditor_national_id: "",
        accountant_firm: "",
        accountant_name: "",
        accountant_national_id: "",
        vat_issued_on: "",
        is_foreign_business: false,
        // ── Foreign Business files (⭐ เปลี่ยนเป็นหลายไฟล์ต่อประเภท) ──
        // รายการไฟล์เดิมที่มีอยู่แล้วใน DB (จาก table incorporate_matter_foreign_business_file)
        // แต่ละ item: { id, file_type, file_name, original_name }
        fbl_existing_list: [],
        fbc_existing_list: [],
        // ไฟล์ใหม่ที่เลือกเพิ่ม (ยังไม่ได้ Save) — array ของ File object
        fbl_new_files: [],
        fbc_new_files: [],
      },

      dialogAdd: false,
      dialogAddPush: false,
      text_color: "text-h5 green--text text-center",
      title: "",
      message: "",
      link: "",
    };
  },
  computed: {
    isIndividual() {
      return this.form.entity_type === "Individual";
    },
    entityTypeLabel() {
      const map = {
        "Limited Company": "Limited Company | บริษัทจำกัด",
        "Public Company Limited": "Public Company Limited | บริษัทมหาชนจำกัด",
        Foundation: "Foundation | มูลนิธิ",
        "Representative Office": "Representative Office | สำนักงานผู้แทนการค้า",
        Individual: "Individual | บุคคลธรรมดา",
      };
      return map[this.form.entity_type] || this.form.entity_type;
    },
    // ⭐ เพิ่มใหม่ — true เมื่อ matter นี้มี docket ผูกอยู่แล้ว (ห้ามแก้ client name)
    hasDockets() {
      return Array.isArray(this.dockets) && this.dockets.length > 0;
    },
  },
  async beforeCreate() {
    this.$store.state.navMenu = true;
  },
  async mounted() {
    this.$hideLoader();
    this.matterId = this.$route.params.id;
    await this.loadClients();
    await this.loadMatter();
    this.pageLoading = false;
  },
  methods: {
    async loadClients() {
      try {
        const company_id = localStorage.getItem(server.COMPANYID);
        const res = await getAllCustomerListByCompany(company_id);
        // ✅ FIX: แปลง id เป็น Number เพื่อให้ === match กับ client_id จาก matter
        this.clientList = (res.data || []).map((c) => ({
          text: c.customer_name || c.name || `Customer #${c.id}`,
          value: Number(c.id),
        }));
      } catch (error) {
        console.error("Error loading clients:", error);
      }
    },

    async loadMatter() {
      try {
        const res = await getIncorporateMatterById(this.matterId);
        const m = res.data;

        // Map data → form
        Object.keys(this.form).forEach((key) => {
          if (m[key] !== undefined && m[key] !== null) {
            this.form[key] = m[key];
          }
        });

        // Shareholders & Directors
        this.form.shareholders = (m.shareholders || []).map((sh) => ({
          full_name: sh.full_name || "",
          nationality: sh.nationality || "",
          ordinary_shares: sh.ordinary_shares || 0,
          preferred_shares: sh.preferred_shares || 0,
        }));
        this.form.directors = (m.directors || []).map((dir) => ({
          full_name: dir.full_name || "",
          nationality: dir.nationality || "",
          authorized: dir.authorized || "No",
        }));

        // ⭐ เก็บรายการ docket — ใช้เช็คว่าควร disable ช่อง Client Name หรือไม่
        this.dockets = m.dockets || [];

        // ⭐ เก็บรายการไฟล์ Foreign Business หลายไฟล์ (จาก table ใหม่)
        // backend ส่ง m.foreign_business_files = [{ id, file_type, file_name, original_name }, ...]
        const allFbFiles = m.foreign_business_files || [];
        this.form.fbl_existing_list = allFbFiles.filter(
          (f) => f.file_type === "license",
        );
        this.form.fbc_existing_list = allFbFiles.filter(
          (f) => f.file_type === "certificate",
        );

        // ⭐ Backward-compat: ถ้า matter เก่าไม่มีข้อมูลใน table ใหม่เลย
        // (ยังไม่เคยถูก migrate) แต่มีไฟล์เดิมในคอลัมน์เก่า ให้แสดงเป็นไฟล์
        // เดี่ยวในรายการไปก่อน (id ใช้ string พิเศษเป็น sentinel เพื่อไม่ชน
        // กับ id จริงที่ backend ส่งมาเป็น integer ปกติ)
        if (
          this.form.fbl_existing_list.length === 0 &&
          m.foreign_business_license_file
        ) {
          this.form.fbl_existing_list.push({
            id: "legacy-fbl",
            file_type: "license",
            file_name: m.foreign_business_license_file,
            original_name: m.foreign_business_license_file,
          });
        }
        if (
          this.form.fbc_existing_list.length === 0 &&
          m.foreign_business_certificate_file
        ) {
          this.form.fbc_existing_list.push({
            id: "legacy-fbc",
            file_type: "certificate",
            file_name: m.foreign_business_certificate_file,
            original_name: m.foreign_business_certificate_file,
          });
        }

        this.form.fbl_new_files = [];
        this.form.fbc_new_files = [];

        // Set selected client ใน autocomplete
        // ใช้ $nextTick เพื่อรอให้ clientList render ใน v-autocomplete ก่อน
        if (m.client_id || m.client_name) {
          this.$nextTick(() => {
            const found = this.clientList.find(
              (c) => c.value === Number(m.client_id),
            );
            if (found) {
              this.selectedClient = found;
            } else if (m.client_name) {
              // ไม่เจอใน list → สร้าง object ชั่วคราวให้แสดงชื่อได้
              this.selectedClient = { text: m.client_name, value: m.client_id };
            }
          });
        }
      } catch (error) {
        console.error("Error loading matter:", error);
      }
    },

    // ── File helper methods ──
    getFileUrl(filename) {
      return `${INCORPORATE_MATTER_FILE_URL}/${this.matterId}/${filename}`;
    },
    isPdf(filename) {
      return filename?.toLowerCase().endsWith(".pdf");
    },

    // ⭐ เปลี่ยนเป็นรองรับหลายไฟล์ — เลือกแล้ว push เข้า array ที่จะอัปโหลดตอน Save
    onFblFileChange(e) {
      const files = Array.from(e.target.files || []);
      this.form.fbl_new_files.push(...files);
      e.target.value = "";
    },
    onFbcFileChange(e) {
      const files = Array.from(e.target.files || []);
      this.form.fbc_new_files.push(...files);
      e.target.value = "";
    },
    // ⭐ ลบไฟล์ใหม่ที่ยังไม่ได้ Save ออกจากรายการ (ตาม index)
    clearNewFile(type, idx) {
      if (type === "fbl") this.form.fbl_new_files.splice(idx, 1);
      if (type === "fbc") this.form.fbc_new_files.splice(idx, 1);
    },
    // ⭐ ลบไฟล์เดิม (ที่บันทึกแล้วใน DB) ทันทีผ่าน API — ไม่ต้องรอกด Save
    async deleteExistingForeignBusinessFile(file) {
      if (
        !confirm(
          `ต้องการลบไฟล์ "${file.original_name || file.file_name}" หรือไม่?`,
        )
      )
        return;

      this.deletingFileId = file.id;
      try {
        if (String(file.id).startsWith("legacy-")) {
          // ไฟล์ legacy (จากคอลัมน์เก่า ยังไม่มี id จริงใน table ใหม่)
          // ลบโดยอัปเดตคอลัมน์เก่าให้เป็น null ผ่าน endpoint update ปกติ
          const fd = new FormData();
          if (file.file_type === "license")
            fd.append("fbl_clear_legacy", "true");
          if (file.file_type === "certificate")
            fd.append("fbc_clear_legacy", "true");
          await updateIncorporateMatter(this.matterId, fd);
        } else {
          await deleteIncorporateMatterForeignBusinessFile(
            this.matterId,
            file.id,
          );
        }

        if (file.file_type === "license") {
          this.form.fbl_existing_list = this.form.fbl_existing_list.filter(
            (f) => f.id !== file.id,
          );
        } else {
          this.form.fbc_existing_list = this.form.fbc_existing_list.filter(
            (f) => f.id !== file.id,
          );
        }
      } catch (error) {
        console.error("Error deleting foreign business file:", error);
        this.setupAlertDialog(
          true,
          "Error",
          "เกิดข้อผิดพลาดในการลบไฟล์",
          "text-h5 red--text text-center",
        );
      } finally {
        this.deletingFileId = null;
      }
    },

    onClientChange(selected) {
      if (selected && selected.value) {
        this.form.client_id = selected.value;
        this.form.client_name = selected.text;
      } else {
        this.form.client_id = null;
        this.form.client_name = "";
      }
    },

    onClientClear() {
      this.selectedClient = null;
      this.form.client_id = null;
      this.form.client_name = "";
    },

    addShareholder() {
      this.form.shareholders.push({
        full_name: "",
        nationality: "",
        ordinary_shares: 0,
        preferred_shares: 0,
      });
    },
    removeShareholder(i) {
      this.form.shareholders.splice(i, 1);
    },
    addDirector() {
      this.form.directors.push({
        full_name: "",
        nationality: "",
        authorized: "-- เลือก --",
      });
    },
    removeDirector(i) {
      this.form.directors.splice(i, 1);
    },

    // ⭐ แก้ไข — เพิ่ม helper กลาง formatThaiNationalId() + method ผูกกับ
    // ทั้ง 3 ฟิลด์เลข 13 หลัก (national_id, auditor_national_id,
    // accountant_national_id) แบบเดียวกับที่แก้ไว้ใน IncorporateMatterAdd.vue
    // เพื่อให้พิมพ์ได้เฉพาะตัวเลขและขึ้นขีดคั่นอัตโนมัติ
    // (รูปแบบ X-XXXX-XXXXX-XX-X) — เดิมไฟล์ Edit นี้ไม่มี logic กรองเลย
    // แม้แต่ฟิลด์เดียว มีแค่ maxlength="17" เฉย ๆ
    formatThaiNationalId(rawValue) {
      let d = (rawValue || "").replace(/\D/g, "").slice(0, 13);
      let result = d;
      if (d.length > 1) result = d[0] + "-" + d.slice(1);
      if (d.length > 5)
        result = result.slice(0, 2) + d.slice(1, 5) + "-" + d.slice(5);
      if (d.length > 10)
        result =
          result.slice(0, 12) + "-" + d.slice(10, 12) + "-" + d.slice(12);
      return result;
    },

    formatNationalId() {
      this.form.national_id = this.formatThaiNationalId(this.form.national_id);
    },

    formatAuditorNationalId() {
      this.form.auditor_national_id = this.formatThaiNationalId(
        this.form.auditor_national_id,
      );
    },

    formatAccountantNationalId() {
      this.form.accountant_national_id = this.formatThaiNationalId(
        this.form.accountant_national_id,
      );
    },

    // ✅ เพิ่ม: validateForm() — ไฟล์ Edit เดิมไม่มีการตรวจสอบฟิลด์บังคับก่อน Save
    validateForm() {
      if (this.isIndividual) {
        if (!this.form.client_name || !this.form.client_name.trim()) {
          this.setupAlertDialog(
            true,
            "Check information",
            "กรุณากรอก Client Name",
            "text-h5 red--text text-center",
          );
          return false;
        }
        if (this.form.id_type === "national_id") {
          if (!this.form.national_id || !this.form.national_id.trim()) {
            this.setupAlertDialog(
              true,
              "Check information",
              "กรุณากรอกเลขบัตรประชาชน (13 หลัก)",
              "text-h5 red--text text-center",
            );
            return false;
          }
        } else {
          if (!this.form.passport_no || !this.form.passport_no.trim()) {
            this.setupAlertDialog(
              true,
              "Check information",
              "กรุณากรอกเลขที่หนังสือเดินทาง",
              "text-h5 red--text text-center",
            );
            return false;
          }
        }
      } else {
        if (!this.form.company_name || !this.form.company_name.trim()) {
          this.setupAlertDialog(
            true,
            "Check information",
            "กรุณากรอก Company Name",
            "text-h5 red--text text-center",
          );
          return false;
        }
        if (!this.form.registration_no || !this.form.registration_no.trim()) {
          this.setupAlertDialog(
            true,
            "Check information",
            "กรุณากรอก Registration No.",
            "text-h5 red--text text-center",
          );
          return false;
        }
        if (
          !this.form.registered_address ||
          !this.form.registered_address.trim()
        ) {
          this.setupAlertDialog(
            true,
            "Check information",
            "กรุณากรอก Registered Address",
            "text-h5 red--text text-center",
          );
          return false;
        }
      }
      return true;
    },

    buildFormData() {
      const fd = new FormData();
      const user_id = localStorage.getItem(server.USER_ID);

      if (this.form.client_id) fd.append("client_id", this.form.client_id);

      const fields = [
        "client_name",
        "client_name_en",
        "entity_type",
        "nationality",
        "status",
        "registration_no",
        "company_name",
        "date_of_incorporation",
        "registered_address",
        "branch",
        "audit_firm",
        "licensed_auditor_name",
        "auditor_license_no",
        "fiscal_year_end",
        "auditor_national_id",
        "accountant_firm",
        "accountant_name",
        "accountant_national_id",
        "vat_issued_on",
        "id_type",
        "full_name",
        "national_id",
        "passport_no",
        "contact_address",
        "contact_number",
        "contact_email",
      ];
      fields.forEach((key) => {
        if (
          this.form[key] !== null &&
          this.form[key] !== undefined &&
          this.form[key] !== ""
        ) {
          fd.append(key, this.form[key]);
        }
      });

      fd.append(
        "registered_capital",
        parseFloat(this.form.registered_capital) || 0,
      );
      fd.append("number_of_shares", parseInt(this.form.number_of_shares) || 0);
      fd.append(
        "par_value_per_share",
        parseFloat(this.form.par_value_per_share) || 0,
      );
      fd.append("paid_up_capital", parseFloat(this.form.paid_up_capital) || 0);
      fd.append("user_update", user_id);
      fd.append(
        "is_foreign_business",
        this.form.is_foreign_business ? "true" : "false",
      );
      fd.append("shareholders", JSON.stringify(this.form.shareholders));
      fd.append("directors", JSON.stringify(this.form.directors));

      // ── File handling (⭐ เปลี่ยนเป็นหลายไฟล์) ──
      // ไฟล์เดิมถูกลบทันทีผ่าน API แล้ว (deleteExistingForeignBusinessFile)
      // ตรงนี้แค่ส่งไฟล์ใหม่ที่ผู้ใช้เพิ่มเข้ามา พร้อมแจ้งจำนวนไฟล์ของแต่ละประเภท
      fd.append("fbl_count", String(this.form.fbl_new_files.length));
      fd.append("fbc_count", String(this.form.fbc_new_files.length));
      this.form.fbl_new_files.forEach((file) => {
        fd.append("file", file);
      });
      this.form.fbc_new_files.forEach((file) => {
        fd.append("file", file);
      });

      return fd;
    },

    async saveEdit() {
      // ✅ เพิ่ม: ตรวจสอบฟิลด์บังคับก่อนบันทึก
      if (!this.validateForm()) return;
      this.loadingSave = true;
      this.$showLoader();
      try {
        const fd = this.buildFormData();
        await updateIncorporateMatter(this.matterId, fd);
        this.$hideLoader();
        this.loadingSave = false;
        this.$store.state.global_push_dialog = true;
        this.setupAlertDialogPush(
          true,
          "Success",
          "บันทึกข้อมูลสำเร็จ",
          "text-h5 green--text text-center",
          `/incorporate-matter-view/${this.matterId}`,
        );
      } catch (error) {
        this.$hideLoader();
        this.loadingSave = false;
        console.error("Error updating matter:", error);
        this.setupAlertDialog(
          true,
          "Error",
          "เกิดข้อผิดพลาดในการบันทึก",
          "text-h5 red--text text-center",
        );
      }
    },

    setupAlertDialog(status, title, message, text_color) {
      this.title = title;
      this.message = message;
      this.dialogAdd = status;
      this.text_color = text_color;
    },
    setupAlertDialogPush(status, title, message, text_color, link) {
      this.title = title;
      this.message = message;
      this.dialogAddPush = status;
      this.text_color = text_color;
      this.link = link;
    },
  },
};
</script>

<style scoped>
.bg-colorth {
  background-color: #8d8d8d !important;
}
.tabletextwhite {
  color: white !important;
}
.form-section-title {
  font-size: 14px;
  font-weight: 700;
  color: #374151;
  border-left: 4px solid #0a7ea5;
  padding-left: 10px;
  margin-top: 8px;
}
.radio-option {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s;
}
.radio-option:hover {
  border-color: #0a7ea5;
  background: #f0f9ff;
}
.radio-selected {
  border-color: #0a7ea5;
  background: #eff6ff;
}
.file-preview-box {
  border: 1.5px solid #bfdbfe;
  border-radius: 8px;
  padding: 10px 14px;
  background: #eff6ff;
}
</style>
